import React from 'react';
import { SelectMenu } from '../../../../ui-kit';
import { useInputOptions } from '../useInputOptions';
import type { LookupInputDef } from '../../../../types/lookupDashboard';

interface LookupInputFieldProps {
  dashboardId?: string;
  input: LookupInputDef;
  value: string;
  /** A name for `value` when it is the one the board was answered for, e.g. "Teterboro". */
  valueName?: string | null;
  onChange: (value: string) => void;
  /** The inputs chosen before this one; suggestions on record with them come first. */
  otherInputs: Record<string, string>;
  recordNoun: string;
  /** Whether the list is open, when the page opens it after an earlier pick. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * One of a lookup board's inputs, as the kit's select: the values on record,
 * most-used first and searchable, plus whatever valid value is typed.
 */
export default function LookupInputField({
  dashboardId, input, value, valueName, onChange, otherInputs, recordNoun, open, onOpenChange,
}: LookupInputFieldProps) {
  const suggestions = useInputOptions({ dashboardId, input, otherInputs, noun: recordNoun });
  const pattern = React.useMemo(() => (input.pattern ? new RegExp(input.pattern) : null), [input.pattern]);

  return (
    <SelectMenu
      label={input.label}
      value={value}
      valueLabel={value && valueName ? `${value} · ${valueName}` : undefined}
      placeholder={input.placeholder ?? undefined}
      options={suggestions.options}
      onChange={onChange}
      onOpen={suggestions.activate}
      onSearchChange={suggestions.setSearch}
      loading={suggestions.loading}
      customValue={(typed) => {
        const next = input.uppercase ? typed.toUpperCase() : typed;
        return !pattern || pattern.test(next) ? next : null;
      }}
      open={open}
      onOpenChange={onOpenChange}
      size="medium"
      align="start"
    />
  );
}
